import { parsePhoneNumber } from 'libphonenumber-js/min';

const PO_BOX_PATTERN = /\b(?:p\.?\s*o\.?\s*box|post\s*office\s*box)\b/i;

export const MIN_SHIPPING_PHONE_ALPHANUMERIC = 10;

/**
 * Envia rejects label buy when a phone has fewer than 10 letters or digits.
 *
 * @param {string} phone
 * @returns {boolean}
 */
export const isValidShippingPhone = phone =>
  String(phone || '').replace(/[^A-Za-z0-9]/g, '').length >= MIN_SHIPPING_PHONE_ALPHANUMERIC;

/**
 * Convert a US phone number to E.164 (for example +13235165026).
 * Returns the trimmed original value when the number cannot be parsed.
 *
 * @param {string} phone
 * @returns {string}
 */
export const normalizeUsPhone = phone => {
  const raw = typeof phone === 'string' ? phone.trim() : '';
  if (!raw) {
    return '';
  }
  try {
    const parsed = parsePhoneNumber(raw, 'US');
    if (parsed && parsed.isValid()) {
      return parsed.number;
    }
  } catch (e) {
    // Keep the original value when parsing fails.
  }
  return raw;
};

const US_STATE_CODES = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'AS',
  'GU',
  'MP',
  'PR',
  'VI',
  'AA',
  'AE',
  'AP',
]);

const US_STATE_NAMES = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  'washington dc': 'DC',
  'washington d c': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'american samoa': 'AS',
  guam: 'GU',
  'northern mariana islands': 'MP',
  'puerto rico': 'PR',
  'virgin islands': 'VI',
  'us virgin islands': 'VI',
  'u s virgin islands': 'VI',
  'armed forces americas': 'AA',
  'armed forces europe': 'AE',
  'armed forces pacific': 'AP',
};

/**
 * Map a US state name or common variant to a 2-letter code.
 * Pass through values that are already a valid code.
 *
 * @param {string} state
 * @returns {string}
 */
export const normalizeUsStateCode = state => {
  if (state == null) {
    return '';
  }
  const trimmed = String(state).trim();
  if (!trimmed) {
    return '';
  }
  const upper = trimmed.toUpperCase();
  if (US_STATE_CODES.has(upper)) {
    return upper;
  }
  const compact = upper.replace(/[^A-Z]/g, '');
  if (compact.length === 2 && US_STATE_CODES.has(compact)) {
    return compact;
  }
  const nameKey = trimmed
    .toLowerCase()
    .replace(/\./g, ' ')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return US_STATE_NAMES[nameKey] || trimmed;
};

/**
 * True when Origin address has every field Envia needs.
 *
 * @param {Object} origin
 * @returns {boolean}
 */
export const isOriginComplete = origin => {
  if (!origin) {
    return false;
  }
  const required = ['name', 'phone', 'street', 'city', 'state', 'postalCode'];
  return (
    required.every(key => typeof origin[key] === 'string' && origin[key].trim().length > 0) &&
    isValidShippingPhone(origin.phone)
  );
};

const EMPTY_SHIPPING_ADDRESS = {
  name: '',
  phone: '',
  street1: '',
  streetNo: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
};

const trimAddressValue = value => (typeof value === 'string' ? value.trim() : '');

/**
 * Map a saved user address (protectedData.shippingAddress or privateData.originAddress)
 * into the shared shipping address form shape.
 *
 * @param {Object} [currentUser]
 * @returns {Object}
 */
export const shippingAddressFromUser = currentUser => {
  const profile = currentUser?.attributes?.profile || {};
  const saved = profile.protectedData?.shippingAddress;
  if (saved && (saved.street1 || saved.street)) {
    return {
      name: trimAddressValue(saved.name),
      phone: normalizeUsPhone(saved.phone),
      street1: trimAddressValue(saved.street1 || saved.street),
      streetNo: trimAddressValue(saved.streetNo),
      city: trimAddressValue(saved.city),
      state: normalizeUsStateCode(saved.state),
      zip: trimAddressValue(saved.zip || saved.postalCode),
      country: trimAddressValue(saved.country) || 'US',
    };
  }

  const origin = profile.privateData?.originAddress;
  if (origin && origin.street) {
    return {
      name: trimAddressValue(origin.name),
      phone: normalizeUsPhone(origin.phone),
      street1: trimAddressValue(origin.street),
      streetNo: '',
      city: trimAddressValue(origin.city),
      state: normalizeUsStateCode(origin.state),
      zip: trimAddressValue(origin.postalCode),
      country: trimAddressValue(origin.country) || 'US',
    };
  }

  return { ...EMPTY_SHIPPING_ADDRESS };
};

/**
 * Envia origin fields from the shared shipping address form shape.
 *
 * @param {Object} address
 * @returns {Object|null}
 */
export const originFromShippingAddress = address => {
  if (!address) {
    return null;
  }
  const street1 = trimAddressValue(address.street1 || address.street);
  const streetNo = trimAddressValue(address.streetNo);
  return {
    name: trimAddressValue(address.name),
    phone: normalizeUsPhone(address.phone),
    street: [street1, streetNo].filter(Boolean).join(', '),
    city: trimAddressValue(address.city),
    state: normalizeUsStateCode(address.state),
    postalCode: trimAddressValue(address.zip || address.postalCode),
    country: trimAddressValue(address.country) || 'US',
  };
};

/**
 * Origin used for Envia labels from the current user's saved shipping address.
 *
 * @param {Object} [currentUser]
 * @returns {Object|null}
 */
export const originFromCurrentUser = currentUser => {
  const address = shippingAddressFromUser(currentUser);
  if (!address?.street1 && !address?.street) {
    return null;
  }
  return originFromShippingAddress(address);
};

/**
 * True when text looks like a US PO Box.
 *
 * @param {Object} address
 * @returns {boolean}
 */
export const isPoBoxAddress = address => {
  const fields = [address?.street, address?.line1, address?.line2, address?.city]
    .filter(Boolean)
    .join(' ');
  return PO_BOX_PATTERN.test(fields);
};

/**
 * Build a public city, state string from a Mapbox place or formatted address.
 *
 * @param {Object} place
 * @returns {string}
 */
export const publicLocationAddress = place => {
  if (place?.city && place?.state) {
    return `${place.city}, ${place.state}`;
  }
  const formatted = place?.address || '';
  const parts = formatted
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  if (parts.length >= 3) {
    const maybeCountry = parts[parts.length - 1];
    const stateZip = parts[parts.length - 2];
    const city = parts[parts.length - 3];
    const state = (stateZip.match(/^([A-Za-z]{2})\b/) || stateZip.match(/^([A-Za-z]+)/) || [])[1];
    if (city && state && /united states|usa/i.test(maybeCountry)) {
      return `${city}, ${state}`;
    }
    if (city && state) {
      return `${city}, ${state}`;
    }
  }
  return formatted;
};

export const pickupAddressFromPlace = (place, building) => ({
  street: place?.street || place?.address || '',
  city: place?.city || '',
  state: place?.state || '',
  postalCode: place?.postalCode || '',
  building: building || '',
});

/**
 * Structured address fields from a geocoder place.
 * State is normalized to a US 2-letter code when possible.
 *
 * @param {Object} place
 * @returns {{street: string, city: string, state: string, postalCode: string}}
 */
export const addressFieldsFromPlace = place => ({
  street: place?.street || (place?.address || '').split(',')[0].trim(),
  city: place?.city || '',
  state: normalizeUsStateCode(place?.state),
  postalCode: place?.postalCode || '',
});

/**
 * Structured origin fields from a geocoder place.
 *
 * @param {Object} place
 * @returns {{street: string, city: string, state: string, postalCode: string}}
 */
export const originFieldsFromPlace = addressFieldsFromPlace;

/**
 * Street line from a LocationAutocompleteInput field value.
 *
 * @param {Object|string} location
 * @returns {string}
 */
export const streetFromLocationField = location => {
  if (!location) {
    return '';
  }
  if (typeof location === 'string') {
    return location.trim();
  }
  if (location.selectedPlace?.street) {
    return location.selectedPlace.street.trim();
  }
  return (location.search || '').trim();
};

export const destinationFromCheckoutValues = values => {
  const street =
    (typeof values.recipientAddressLine1 === 'string' && values.recipientAddressLine1.trim()) ||
    streetFromLocationField(values.recipientLocation);
  return {
    name: values.recipientName,
    phone: normalizeUsPhone(values.recipientPhoneNumber),
    street,
    line1: street,
    line2: values.recipientAddressLine2,
    city: values.recipientCity,
    state: values.recipientState,
    postalCode: values.recipientPostal,
    country: values.recipientCountry || 'US',
  };
};

/**
 * Checkout form values from a saved shipping address (protectedData shape).
 *
 * @param {Object} [address]
 * @returns {Object}
 */
export const checkoutValuesFromShippingAddress = address => {
  if (!address) {
    return {};
  }
  return {
    recipientName: trimAddressValue(address.name),
    recipientPhoneNumber: normalizeUsPhone(address.phone),
    recipientAddressLine1: trimAddressValue(address.street1 || address.street),
    recipientAddressLine2: trimAddressValue(address.streetNo),
    recipientCity: trimAddressValue(address.city),
    recipientState: normalizeUsStateCode(address.state),
    recipientPostal: trimAddressValue(address.zip || address.postalCode),
    recipientCountry: trimAddressValue(address.country) || 'US',
  };
};

/**
 * Envia destination from a saved shipping address.
 *
 * @param {Object} [address]
 * @returns {Object}
 */
export const destinationFromSavedShippingAddress = address =>
  destinationFromCheckoutValues(checkoutValuesFromShippingAddress(address));

export const isCheckoutDestinationComplete = dest =>
  ['name', 'phone', 'street', 'city', 'state', 'postalCode'].every(
    key => typeof dest?.[key] === 'string' && dest[key].trim().length > 0
  ) && isValidShippingPhone(dest.phone);

/**
 * True when a saved checkout shipping address has every required field.
 *
 * @param {Object} [address]
 * @returns {boolean}
 */
export const isSavedShippingAddressComplete = address =>
  isCheckoutDestinationComplete(destinationFromSavedShippingAddress(address));

const CARRIER_TRACK_URLS = {
  ups: trackingNumber => `https://www.ups.com/track?tracknum=${encodeURIComponent(trackingNumber)}`,
  usps: trackingNumber =>
    `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(trackingNumber)}`,
  fedex: trackingNumber =>
    `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(trackingNumber)}`,
};

const UNUSABLE_TRACK_HOSTS = new Set(['test.envia.com']);

const isUsableTrackUrl = url => {
  if (typeof url !== 'string' || !url.trim()) {
    return false;
  }
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && !UNUSABLE_TRACK_HOSTS.has(parsed.hostname.toLowerCase());
  } catch (e) {
    return false;
  }
};

/**
 * Usable Envia track URL, or a carrier page built from the tracking number.
 *
 * @param {Object} shipping
 * @returns {string|null}
 */
export const trackUrlForShipment = shipping => {
  if (isUsableTrackUrl(shipping?.trackUrl)) {
    return shipping.trackUrl;
  }
  const trackingNumber = shipping?.trackingNumber;
  if (!trackingNumber) {
    return null;
  }
  const builder = CARRIER_TRACK_URLS[String(shipping.carrier || '').toLowerCase()];
  return builder ? builder(trackingNumber) : null;
};

/**
 * Shipping status shown on the order page from transaction metadata.
 *
 * @param {Object} shipping
 * @param {string} [fallbackCarrier]
 * @returns {Object|null}
 */
export const shippingStatusFromMetadata = (shipping, fallbackCarrier) => {
  if (!shipping || typeof shipping !== 'object') {
    return null;
  }
  const purchased = shipping.labelStatus === 'purchased';
  return {
    labelStatus: shipping.labelStatus || 'pending',
    carrier: shipping.carrier || fallbackCarrier || null,
    service: shipping.service || null,
    trackingNumber: purchased ? shipping.trackingNumber || null : null,
    trackUrl: purchased ? trackUrlForShipment(shipping) : null,
    hasLabel: purchased,
    scanned: !!shipping.scannedAt,
  };
};
